import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import type { ModuleDefinition } from '../../config/moduleCatalog';

/**
 * The competitive "Opener's 3rd bid" chooser (feature 023, FR-027).
 *
 * This module does not serve its own pool — it reuses the existing "Decision to
 * make a 3rd level bid" practice page. That page is split into opener-based
 * sub-modes, so the button opens a two-option chooser and navigates to
 * `/problems?mode=3rd-level-bid&subMode=…`, exactly the URL the retired
 * dashboard accordion used to produce.
 *
 * The "precision" sub-mode additionally needs an opening bid. Rather than
 * rebuild the six-way radio group here, the chooser routes to the sub-mode and
 * lets the practice page resolve its default — the previous chooser required a
 * radio selection, so this is a genuine behaviour change and it is the reason
 * `resolveThirdLevelSubMode` already tolerates a missing `openingBid`.
 */

export interface ThirdBidChooserProps {
  module: ModuleDefinition;
}

const BTN =
  'min-w-[92px] rounded-lg bg-primary-600 px-[18px] py-2.5 text-center text-[13px] font-bold text-white ' +
  'transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-primary-600 focus-visible:ring-offset-2';

export function ThirdBidChooser({ module }: ThirdBidChooserProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEscapeClose(close, open);

  const variants = module.redirect?.variants ?? [];
  const route = module.redirect?.route ?? '/problems';

  return (
    // `phone-pt:static` hands the panel's containing block to the module row (which is
    // `relative`). On a phone in portrait the Practice button sits ~106px in from the
    // card's right edge — Master and its gap are to its right — so a panel right-aligned
    // on the BUTTON runs off the left of the screen. Anchored to the card instead, the
    // 16rem panel lands inside the row at every phone width. Desktop keeps the button as
    // the anchor, where the panel has always hung.
    <div className="relative phone-pt:static">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="third-bid-chooser-panel"
        data-testid={`module-practice-${module.slug}`}
        className={BTN}
      >
        Practice
      </button>

      {open && (
        <div
          id="third-bid-chooser-panel"
          data-testid="third-bid-chooser-panel"
          className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-line bg-card p-3 shadow-lg"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-soft">
            Which auctions?
          </p>
          {variants.map((v) => (
            <button
              key={v.subMode}
              type="button"
              data-testid={`third-bid-variant-${v.subMode}`}
              onClick={() => navigate(`${route}?mode=3rd-level-bid&subMode=${v.subMode}`)}
              className="mb-2 w-full rounded-lg border border-line-strong bg-cream px-4 py-2.5 text-left text-sm font-semibold text-ink transition-colors hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
